<template>
  <div class="container">
    <div class="row terms-box">
      <div class="col-lg-12">
        <!-- ローディング状態 -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading...</p>
        </div>

        <!-- エラー状態 -->
        <div v-else-if="error" class="error-container">
          <h1>An error occurred.</h1>
          <p>{{ error }}</p>
          <button @click="fetchPrivacyContent" class="retry-button">Retry</button>
        </div>

        <!-- コンテンツ表示 -->
        <div v-else class="privacy-content" v-html="privacyContent"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import axiosBase from "axios";
import { getLocaleForLegalDocuments } from "@/lib/utils/i18n/locale";

// microCMS APIのレスポンス型定義
interface MicroCMSPrivacyContent {
  id: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  revisedAt: string;
  type: string[];
  html: string;
  locale: string[];
}

interface MicroCMSResponse {
  contents: MicroCMSPrivacyContent[];
}

// axiosインスタンスの作成
const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend({
  // NOTE: metaタグの設定
  metaInfo() {
    if (process.env.NODE_ENV === "production") {
      return {};
    }
    return {
      meta: [
        {
          name: "robots",
          content: "none",
        },
      ],
    };
  },
  data() {
    return {
      isLoading: true,
      error: null as string | null,
      privacyContent: null as string | null,
    };
  },
  async created() {
    await this.fetchPrivacyContent();
  },
  methods: {
    async fetchPrivacyContent() {
      try {
        this.isLoading = true;
        this.error = null;

        // プライバシーポリシー用のロケールを取得（Intl APIを使用）
        const microCMSLocale = getLocaleForLegalDocuments();

        // フィルターを構築
        const localeFilter = `type[contains]privacy[and]locale[contains]${microCMSLocale}`;

        // microCMSからプライバシーポリシーを取得
        // type=privacyとlocaleで絞り込んで取得
        const privacyRequest = await axios.get<MicroCMSResponse>(`/docs?filters=${localeFilter}`);

        if (privacyRequest.status !== 200) {
          throw new Error("Failed to fetch privacy policy");
        }

        const responseData = privacyRequest.data;

        // データの存在確認とバリデーション
        if (!responseData?.contents || !Array.isArray(responseData.contents) || responseData.contents.length === 0) {
          throw new Error("Privacy policy data not found");
        }

        // 最初のコンテンツを取得
        const privacy = responseData.contents[0];

        if (!privacy?.html) {
          throw new Error("Privacy policy HTML content not found");
        }

        // microCMSから取得したHTMLをそのまま設定
        this.privacyContent = privacy.html;
      } catch (error) {
        console.error("Failed to fetch privacy content:", error);
        this.error = error instanceof Error ? error.message : "An unknown error occurred";
      } finally {
        this.isLoading = false;
      }
    },
  },
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.terms-box {
  background-color: var(--color-surface-subtle);
  padding: 100px 13% 50px 13%;
  border: solid 10px var(--color-border);
  color: var(--color-text-normal);
}

/* ローディング状態のスタイル */
.loading-container {
  text-align: center;
  padding: 50px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--color-primary);
  border-radius: var(--radius-circle);
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  color: var(--color-text-low);
  font-size: var(--font-size-md);
}

/* エラー状態のスタイル */
.error-container {
  text-align: center;
  padding: 50px 0;
}

.error-container h1 {
  color: var(--color-warning);
  font-size: var(--font-size-xl);
  margin-bottom: 20px;
}

.error-container p {
  color: var(--color-text-low);
  font-size: var(--font-size-md);
  margin-bottom: 30px;
}

.retry-button {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  border: none;
  padding: 12px 24px;
  border-radius: var(--radius-xs);
  cursor: pointer;
  font-size: var(--font-size-md);
  transition: background-color 0.3s;
}

.retry-button:hover {
  opacity: 0.9;
}

/* スマホ画面レスポンシブ対応 */
@media screen and (max-width: 768px) {
  .terms-box {
    padding: 60px 5% 40px 5%;
  }
}

@media screen and (max-width: 576px) {
  .terms-box {
    padding: 50px 15px;
  }

  .loading-container,
  .error-container {
    padding: 30px 15px;
  }

  .error-container h1 {
    font-size: var(--font-size-lg);
  }

  .loading-container p,
  .error-container p {
    font-size: var(--font-size-sm);
  }
}
</style>
